/**
 * Classify a playback state change as user-initiated vs framework-generated.
 *
 * In master-timeline videos (interactive-edu / master-narration) the controller
 * legitimately moves the playhead on every slide transition. Inferring intent
 * from currentTime deltas alone mislabels those framework jumps as user scrubs,
 * pollutes the funnel, and fabricates pause/resume. The controller knows the
 * real cause and stamps it on the state as `changeSource`; these helpers turn
 * that into honest analytics labels. When `changeSource` is absent (older
 * controller build) we fall back to the legacy delta heuristic.
 */

export type ChangeSource =
  | 'tick'
  | 'user_play'
  | 'user_pause'
  | 'user_seek'
  | 'user_scrub'
  | 'user_nav'
  | 'auto_advance'
  | 'transition';

const USER_SEEK_VIA: Record<string, string> = {
  user_seek: 'seek',
  user_scrub: 'scrub',
  user_nav: 'slide_nav',
};

export interface SeekClass {
  via: string;
  /** True only when a human moved the playhead (drag/seek/slide-click). */
  user: boolean;
}

/**
 * Decide whether a playhead change is a seek and, if so, how to label it.
 * Returns null when it is not a seek (normal playback tick, or a play/pause).
 */
export function classifySeek(
  source: ChangeSource | undefined,
  prevTime: number,
  currTime: number,
  sameSlide: boolean,
): SeekClass | null {
  if (source && source in USER_SEEK_VIA) return { via: USER_SEEK_VIA[source], user: true };
  if (source === 'auto_advance' || source === 'transition') {
    // A real playhead jump caused by the framework advancing/transitioning slides.
    return Math.abs(currTime - prevTime) > 1 ? { via: source, user: false } : null;
  }
  if (source === 'user_play' || source === 'user_pause') return null;
  // Legacy fallback: no source stamped → infer from the delta as before.
  const d = currTime - prevTime;
  if (d < -1 || d > 2.5) return { via: sameSlide ? 'scrub' : 'slide_nav', user: true };
  return null;
}

/**
 * Is an isPlaying flip the framework's own transition pause/resume (not a human)?
 * Used to tag video_pause / video_resume so framework churn isn't counted as
 * user pauses.
 */
export function isFrameworkPlayChange(source: ChangeSource | undefined): boolean {
  return source === 'auto_advance' || source === 'transition';
}
